import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { useParams } from '@tanstack/react-router';
import type { OrganizationDto } from '@uturn/api/v1';
import {
	OrganizationSettingsDtoContainerStatus,
	OrganizationSettingsDtoPreferredDistanceUnit,
	OrganizationSettingsDtoShipmentCondition,
	OrganizationSettingsDtoTransportType,
	useFetchOrganizationByIdSuspense,
	useUpdateOrganization,
} from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import { queryClient } from '@uturn/portal/query';
import { isSelectableShippingLine } from '@uturn/portal/types/shipping-line.typeguard';
import {
	Button,
	Combobox,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	Icon,
	RadioGroup,
	RadioGroupItem,
	Separator,
	sonner,
} from '@uturn/ui-kit';
import { Suspense, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function OrganizationShipmentSettings() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: '/_protected/org/$orgId/settings/shipment-settings',
	});
	const { shipmentMetaData } = useContext(MetaDataContext);
	const shipmentShippingLines = shipmentMetaData?.shippingLines ?? [];

	const { data: organization, queryKey } = useFetchOrganizationByIdSuspense(
		orgId,
		{
			query: {
				select: (data) => data.data,
			},
		},
	);

	// No zod schema: all fields are closed-set radio/combobox enums — nothing to validate.
	const form = useForm<OrganizationDto>({
		values: organization,
	});

	const { mutate, isPending } = useUpdateOrganization({
		mutation: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey });

				sonner.success(
					t(
						'pages.account.organisation.shipment-settings.form.submit.success.description',
						'Organisation has been updated!',
					),
				);
			},
			onError: () => {
				sonner.error(t('general.try-again-later', 'Please try again later.'));
			},
		},
	});

	const onSubmit = (formData: OrganizationDto) => {
		mutate({ id: orgId, data: formData });
	};

	// TODO: Move or remove this helper function if API can return the correct type
	const formatBooleanValue = ({
		value,
		options,
	}: {
		value: boolean | undefined | null;
		options: [string, string, ...string[]];
	}) => {
		if (value === undefined || value === null) {
			return '';
		}
		return value ? options[0] : options[1];
	};

	return (
		<>
			<Helmet
				title={t(
					'pages.account.organisation.shipment-settings.title',
					'Organisation - Settings',
				).toString()}
			/>
			<Suspense
				fallback={
					<p>
						{t(
							'pages.account.organisation.shipment-settings.loading',
							'Loading shipment settings',
						)}
					</p>
				}
			>
				<div className="space-y-6">
					<div>
						<h3 className="text-lg font-medium">
							{t(
								'pages.account.organisation.shipment-settings.title',
								'Shipment settings',
							)}
						</h3>
						<p className="text-muted-foreground text-sm">
							{t(
								'pages.account.organisation.shipment-settings.description',
								'These settings will be used as default for the creation of all your shipments.',
							)}
						</p>
					</div>
					<Separator />
					<Form {...form}>
						<form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
							<FormField
								control={form.control}
								name="settings.shippingLineId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'pages.account.organisation.shipment-settings.form.shipping-line.label',
												'Shipping line',
											)}
										</FormLabel>
										<FormControl>
											<Combobox
												defaultValue={field.value as number} // Guaranteed by options.value (shippingLine.id)
												onChange={(value) => field.onChange(value ?? -1)}
												options={shipmentShippingLines
													.filter(isSelectableShippingLine)
													.map((item) => ({
														value: item.id,
														label: `${item.description} (${item.code})`,
													}))}
												nullable={true}
												placeholder={t(
													'pages.account.organisation.shipment-settings.form.shipping-line.placeholder',
													'Select shipping line',
												).toString()}
												searchPlaceholder={t(
													'pages.account.organisation.shipment-settings.form.shipping-line.search.placeholder',
													'Search shipping line...',
												).toString()}
												emptyMessage={t(
													'pages.account.organisation.shipment-settings.form.shipping-line.search.empty',
													'No shipping line found.',
												).toString()}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="settings.invoiceRequired"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'pages.account.organisation.shipment-settings.form.invoice-ref-required.label',
												'Invoice reference required',
											)}
										</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(e) => {
													field.onChange(e === 'yes');
												}}
												value={formatBooleanValue({
													value: field.value,
													options: ['yes', 'no'],
												})}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="yes" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.invoice-ref-required.values.yes',
															'Yes',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="no" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.invoice-ref-required.values.no',
															'No',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="settings.transportType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'pages.account.organisation.shipment-settings.form.transport-type.label',
												'Transport type',
											)}
										</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(e) => {
													field.onChange(e);
												}}
												value={field.value}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoTransportType.IMPORT
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.transport-type.values.import',
															'Import',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoTransportType.EXPORT
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.transport-type.values.export',
															'Export',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={OrganizationSettingsDtoTransportType.SHUNT}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.transport-type.values.shunt',
															'Shunt',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="settings.fixedPrice"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'pages.account.organisation.shipment-settings.form.carrier-can.label',
												'Carrier can',
											)}
										</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(e) => {
													field.onChange(e === 'bid-accept');
												}}
												value={formatBooleanValue({
													value: field.value,
													options: ['bid-accept', 'only-bid'],
												})}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="only-bid" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.carrier-can.values.bid-only',
															'Bid only',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="bid-accept" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.carrier-can.values.bid-only-accept',
															'Bid or accept',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="settings.requiresVat"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'pages.account.organisation.shipment-settings.form.goods-cleared.label',
												'Goods cleared',
											)}
										</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(e) => {
													field.onChange(e === 'regular');
												}}
												value={formatBooleanValue({
													value: field.value,
													options: ['regular', 'free'],
												})}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="free" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.goods-cleared.values.free-vat',
															'Free of VAT',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="regular" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.goods-cleared.values.regular-vat',
															'Regular VAT',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="settings.containerStatus"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'pages.account.organisation.shipment-settings.form.container-status.label',
												'Container status',
											)}
										</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(e) => {
													field.onChange(e);
												}}
												value={field.value}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoContainerStatus.FULL
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.container-status.values.full',
															'Full',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoContainerStatus.EMPTY
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.container-status.values.empty',
															'Empty',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="settings.shipmentCondition"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'pages.account.organisation.shipment-settings.form.shipment-conditions.label',
												'Terms and conditions',
											)}
										</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(e) => {
													field.onChange(e);
												}}
												value={field.value}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoShipmentCondition.AVC
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.shipment-conditions.values.avc',
															'AVC',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoShipmentCondition.CMR
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.account.organisation.shipment-settings.form.shipment-conditions.values.cmr',
															'CMR',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoShipmentCondition.RHA
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'general.default-shipment-values.shipment-conditions.values.rha',
															'RHA',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="settings.preferredDistanceUnit"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t(
												'organisation.default-shipment-values.form.preferred-distance-unit.label',
												'Preferred distance unit',
											)}
										</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={field.onChange}
												value={field.value}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoPreferredDistanceUnit.kilometer
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'organisation.default-shipment-values.form.preferred-distance-unit.values.km',
															'Kilometers',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem
															value={
																OrganizationSettingsDtoPreferredDistanceUnit.mile
															}
														/>
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'organisation.default-shipment-values.form.preferred-distance-unit.values.mi',
															'Miles',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									</FormItem>
								)}
							/>
							<Button type="submit" disabled={isPending}>
								{t('general.actions.save', 'Save')}
								{isPending && (
									<Icon spin className="ml-3" icon={faSpinnerThird} />
								)}
							</Button>
						</form>
					</Form>
				</div>
			</Suspense>
		</>
	);
}
